import { idbGet, idbSet } from "../cache/idb.js";

// ASOS rain gauges near the radar from the Iowa Environmental Mesonet, for a
// side-by-side check of the radar total. Totals come from routine hourly
// METAR p01i (rain since the previous routine ob), summed over whole
// routine-to-routine hours inside the window, so each gauge reports its own
// exact window [w0, w1]. Ported from tools/rain-validation/lib/gauges/asos.js.

const IEM = "https://mesonet.agron.iastate.edu";
const MM_PER_IN = 25.4;
const TRACE = 0.0001;
const MIN_MS = 60 * 1000;
const HOUR_MS = 60 * MIN_MS;
const DEG = Math.PI / 180;
const META_TTL_MS = 30 * 24 * HOUR_MS;
const RECENT_MS = 3 * 24 * HOUR_MS; // newer windows may still be filling in

const PGROUP = /\sP\d{4}(?=\s|$)/;
const PRECIP_WX = /RA|DZ|UP|GR|GS/;
const FROZEN_WX = /(^|\s|[-+])(?:VC)?(?:SH|BL|DR|TS)?(SN|SG|PL|GS|GR|IC|UP)|FZ/;

// Padded CONUS state boxes [minLon, minLat, maxLon, maxLat]; only used to pick
// which IEM *_ASOS networks to fetch.
const STATE_BOXES = {
  AL: [-88.7, 30.0, -84.7, 35.2], AZ: [-115.0, 31.1, -108.8, 37.2], AR: [-94.8, 32.8, -89.4, 36.7],
  CA: [-124.6, 32.3, -113.9, 42.2], CO: [-109.3, 36.8, -101.8, 41.2], CT: [-73.9, 40.8, -71.6, 42.3],
  DE: [-75.9, 38.3, -74.9, 40.0], FL: [-87.8, 24.3, -79.8, 31.2], GA: [-85.8, 30.2, -80.6, 35.2],
  ID: [-117.4, 41.8, -110.8, 49.2], IL: [-91.7, 36.8, -87.3, 42.7], IN: [-88.3, 37.6, -84.6, 41.9],
  IA: [-96.8, 40.2, -89.9, 43.7], KS: [-102.3, 36.8, -94.4, 40.2], KY: [-89.8, 36.3, -81.8, 39.3],
  LA: [-94.2, 28.8, -88.6, 33.2], ME: [-71.3, 42.8, -66.7, 47.7], MD: [-79.7, 37.7, -74.8, 39.9],
  MA: [-73.7, 41.1, -69.7, 43.1], MI: [-90.6, 41.5, -82.2, 48.5], MN: [-97.4, 43.3, -89.3, 49.6],
  MS: [-91.9, 30.0, -87.9, 35.2], MO: [-95.9, 35.8, -88.9, 40.8], MT: [-116.2, 44.2, -103.8, 49.2],
  NE: [-104.3, 39.8, -95.1, 43.2], NV: [-120.2, 34.8, -113.9, 42.2], NH: [-72.8, 42.5, -70.4, 45.5],
  NJ: [-75.8, 38.8, -73.7, 41.6], NM: [-109.3, 31.1, -102.8, 37.2], NY: [-80.0, 40.3, -71.6, 45.2],
  NC: [-84.5, 33.6, -75.3, 36.8], ND: [-104.3, 45.7, -96.4, 49.2], OH: [-85.0, 38.2, -80.3, 42.1],
  OK: [-103.2, 33.4, -94.2, 37.2], OR: [-124.8, 41.8, -116.3, 46.5], PA: [-80.7, 39.5, -74.5, 42.5],
  RI: [-71.9, 41.1, -71.0, 42.2], SC: [-83.6, 31.9, -78.3, 35.4], SD: [-104.3, 42.3, -96.2, 46.1],
  TN: [-90.5, 34.8, -81.4, 36.9], TX: [-106.9, 25.6, -93.3, 36.7], UT: [-114.3, 36.8, -108.8, 42.2],
  VT: [-73.6, 42.5, -71.3, 45.2], VA: [-83.9, 36.3, -75.0, 39.7], WA: [-124.9, 45.4, -116.7, 49.2],
  WV: [-82.8, 37.0, -77.5, 40.8], WI: [-93.1, 42.3, -86.6, 47.3], WY: [-111.3, 40.8, -103.9, 45.2],
};

export function haversineM(lat1, lon1, lat2, lon2) {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const a = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(((lon2 - lon1) * DEG) / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function statesIntersecting(lat, lon, radiusM) {
  const dLat = radiusM / 111000;
  const dLon = radiusM / (111000 * Math.cos(Math.min(89, Math.abs(lat) + dLat) * DEG));
  return Object.entries(STATE_BOXES)
    .filter(([, [x0, y0, x1, y1]]) => !(x1 < lon - dLon || x0 > lon + dLon || y1 < lat - dLat || y0 > lat + dLat))
    .map(([s]) => s);
}

export function parseCsv(text) {
  const lines = String(text || "").split(/\r?\n/).filter((l) => l.length && !l.startsWith("#"));
  if (!lines.length) return [];
  const head = lines[0].split(",");
  return lines.slice(1).map((l) => {
    const cells = l.split(",");
    const row = {};
    head.forEach((h, i) => (row[h] = cells[i]));
    return row;
  });
}

function parseIemTime(s) {
  const m = s && s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : NaN;
}

const isoMinute = (ms) => `${new Date(ms).toISOString().slice(0, 16)}Z`;

async function fetchText(url, signal) {
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`IEM request failed (${resp.status})`);
  return resp.text();
}

async function loadStations({ lat, lon, radiusM, dateMs, signal }) {
  const out = new Map();
  for (const st of statesIntersecting(lat, lon, radiusM)) {
    const key = `iem:asosmeta:v1:${st}`;
    const cached = await idbGet(key);
    let gj = cached?.value && Date.now() - cached.value.fetchedMs < META_TTL_MS ? cached.value.gj : null;
    if (!gj) {
      gj = JSON.parse(await fetchText(`${IEM}/geojson/network/${st}_ASOS.geojson`, signal));
      idbSet(key, { gj, fetchedMs: Date.now() }, 0);
    }
    for (const f of gj?.features || []) {
      const [flon, flat] = f.geometry?.coordinates || [];
      if (!Number.isFinite(flat)) continue;
      const d = haversineM(lat, lon, flat, flon);
      if (d > radiusM) continue;
      const p = f.properties || {};
      const attrs = p.attributes || {};
      if (attrs.HAS_PHOUR !== undefined && String(attrs.HAS_PHOUR) !== "1") continue;
      if (p.archive_begin && Date.parse(p.archive_begin) > dateMs) continue;
      if (p.archive_end && Date.parse(p.archive_end) < dateMs) continue;
      out.set(f.id, { id: f.id, name: p.sname || f.id, lat: flat, lon: flon, rangeKm: d / 1000 });
    }
  }
  return [...out.values()];
}

async function fetchRoutineObs(ids, stsMs, etsMs, signal) {
  const url = `${IEM}/cgi-bin/request/asos.py?station=${ids.join(",")}&data=p01i,tmpf,wxcodes,metar`
    + `&sts=${isoMinute(stsMs)}&ets=${isoMinute(etsMs)}&tz=UTC&format=onlycomma&report_type=3&missing=M`;
  const byStation = new Map();
  for (const row of parseCsv(await fetchText(url, signal))) {
    const t = parseIemTime(row.valid);
    if (!Number.isFinite(t)) continue;
    if (!byStation.has(row.station)) byStation.set(row.station, []);
    byStation.get(row.station).push({
      t,
      p01i: row.p01i === "M" || row.p01i === undefined ? NaN : +row.p01i,
      tmpf: row.tmpf === "M" ? NaN : +row.tmpf,
      wx: row.wxcodes === "M" ? "" : row.wxcodes || "",
      metar: row.metar || "",
    });
  }
  for (const obs of byStation.values()) obs.sort((a, b) => a.t - b.t);
  return byStation;
}

// Sums whole routine-to-routine hours inside [startMs, endMs]. Returns
// { status, w0, w1, mm, hourly, frozen } where status is "ok", "no_data",
// "no_gauge" (reports rain in present weather but never a P group, as AWOS
// sites without a gauge do, so 0.00 would be a false zero), "incomplete" (a
// missing, sensor-out or irregular hour) or "short" (no whole hour fits).
export function gaugeWindowTotal(obs, startMs, endMs) {
  if (!obs.length) return { status: "no_data" };
  const routine = obs.filter((o, i, a) => i === 0 || o.t - a[i - 1].t > 5 * MIN_MS);
  const hasP = obs.some((o) => PGROUP.test(o.metar) || o.p01i > 0);
  if (!hasP && obs.some((o) => PRECIP_WX.test(o.wx))) return { status: "no_gauge" };
  const hourly = [];
  let frozen = false;
  let incomplete = false;
  for (let i = 1; i < routine.length; i++) {
    const a = routine[i - 1];
    const b = routine[i];
    if (a.t < startMs || b.t > endMs) continue;
    const dtMin = (b.t - a.t) / MIN_MS;
    if (dtMin < 40 || dtMin > 80 || /\bPNO\b/.test(b.metar) || !Number.isFinite(b.p01i)) incomplete = true;
    if (FROZEN_WX.test(b.wx) || (Number.isFinite(b.tmpf) && b.tmpf <= 33)) frozen = true;
    const mm = b.p01i === TRACE || !Number.isFinite(b.p01i) ? 0 : b.p01i * MM_PER_IN;
    hourly.push({ w0: a.t, w1: b.t, mm });
  }
  if (!hourly.length) return { status: "short" };
  for (let i = 1; i < hourly.length; i++) if (hourly[i].w0 !== hourly[i - 1].w1) incomplete = true;
  const mm = hourly.reduce((n, h) => n + h.mm, 0);
  return { status: incomplete ? "incomplete" : "ok", w0: hourly[0].w0, w1: hourly[hourly.length - 1].w1, mm, hourly, frozen };
}

// [{ id, name, lat, lon, rangeKm, status, w0, w1, mm, hourly, frozen }]
export async function loadGaugeTotals({ station, startMs, endMs, radiusM = 230000, signal }) {
  const cacheKey = `iem:gauges:v1:${station.id}:${startMs}:${endMs}`;
  const historical = Date.now() - endMs > RECENT_MS;
  if (historical) {
    const cached = await idbGet(cacheKey);
    if (cached?.value) return cached.value;
  }
  const stations = await loadStations({ lat: station.lat, lon: station.lon, radiusM, dateMs: startMs, signal });
  if (!stations.length) return [];
  const obsByStation = await fetchRoutineObs(stations.map((s) => s.id), startMs - 2 * HOUR_MS, endMs + HOUR_MS, signal);
  const gauges = stations.map((s) => ({ ...s, ...gaugeWindowTotal(obsByStation.get(s.id) || [], startMs, endMs) }));
  if (historical) idbSet(cacheKey, gauges, 0);
  return gauges;
}
